import { useMapGLContext } from "@wayz/react-gl"
import { useCallback } from "react"
import type { StoreRanking } from "@/api"
import { unlockSingleStore, getStoreList, IndustryDataRange, getSurplusQuota } from "@/api"
import { message, Modal } from "antd"
import {
  useBrandPoiModel,
  useQuotaModel,
  useConfigModel,
  useStoreListModel,
  useSelectionModel,
  useCommonModel,
} from "@/models"
import { storeLimit, viewOffset, viewZoom } from "@/constants/constant"

function useQuotaTools() {
  const { setViewState } = useMapGLContext()
  const { setBrandPoiValue } = useBrandPoiModel()
  const { quota, setQuota } = useQuotaModel()
  const { industryCategory } = useConfigModel()
  const { setStoreList } = useStoreListModel()
  const { setAllState: setSelectionAllState } = useSelectionModel()
  const { clearShopCache } = useCommonModel()

  // 获取单网点额度
  const getQuota = useCallback(
    // dataRange: dataRange或者singleAuth
    async (cityId: string, brandId: string, dataRange?: IndustryDataRange | boolean) => {
      if (
        !dataRange ||
        (typeof dataRange === "string" && dataRange !== IndustryDataRange.STORE_RANGE)
      )
        return
      const result = await getSurplusQuota({
        cityId,
        brandId,
        dataRange,
      })

      setQuota(result)
    },
    [setQuota],
  )

  // 缓存单网点
  const cacheQuota = useCallback(
    (poiItem: StoreRanking) => {
      // 清除智能选址网点
      setSelectionAllState({
        activeSitePoint: null,
      })
      // 清除所有缓存店铺
      clearShopCache()
      // 缓存网点
      setBrandPoiValue({ ...poiItem })
    },
    [setSelectionAllState, clearShopCache, setBrandPoiValue],
  )

  // 清除单网点
  const clearQuota = useCallback(() => {
    // 清除智能选址网点
    setSelectionAllState({
      activeSitePoint: null,
    })
    // 清除所有缓存店铺
    clearShopCache()
    // 缓存网点
    setBrandPoiValue(null)
  }, [setSelectionAllState, clearShopCache, setBrandPoiValue])

  // 开通单网点
  const openQuota = useCallback(
    (
      poiItem: StoreRanking,
      cityId: string,
      brandId: string,
      dataRange = undefined,
      isGetList = true,
      callback?: (result: boolean) => void,
    ) => {
      if (quota) {
        const { poiName, poiId } = poiItem
        // const type = getCurrentPageType()

        Modal.confirm({
          title: `当前剩余可解锁${industryCategory}数${quota}，是否消耗一个额度解锁${poiName}${industryCategory}权限?`,
          onOk: async () => {
            try {
              // 解锁某个网点权限api
              const result = await unlockSingleStore({ brandId, cityId, poiId })
              if (result) {
                // 获取单网点额度
                getQuota(cityId, brandId, dataRange)
                // 是否请求getStoreList接口
                if (isGetList) {
                  // 查询网点列表
                  const data = await getStoreList({
                    brandId,
                    cityId,
                    isAsc: false,
                    limit: storeLimit,
                    // moduleType: getMenuKeyByPath(type),
                    page: 0,
                    scale: 2000,
                  })
                  // unstable_batchedUpdates(() => {
                  // 更新store
                  setStoreList(data || [])
                  // 缓存网点
                  cacheQuota(poiItem)
                  // 定位
                  const { longitude, latitude } = poiItem
                  setViewState({
                    longitude: longitude + viewOffset,
                    latitude,
                    zoom: viewZoom,
                  })
                  // })
                }
                // 回调
                callback?.(true)
              } else {
                message.warn("开通失败")
                // 回调
                callback?.(false)
              }
            } catch (error) {
              // 回调
              callback?.(false)
            }
          },
        })
      } else {
        message.warn(`额度余额不足，无权限查看该${industryCategory}数据！`)
        // 回调
        callback?.(false)
      }
    },
    [quota, industryCategory, setStoreList, setViewState, getQuota, cacheQuota],
  )

  return {
    getQuota,
    cacheQuota,
    openQuota,
    clearQuota,
  }
}

export default useQuotaTools
